import React, { useState, useEffect } from 'react';

interface KBItem { id: number; question: string; answer: string; source: string; }
interface KBSections { [section: string]: KBItem[]; }

const KnowledgeBase: React.FC<{ token: string }> = ({ token }) => {
  const [sections, setSections] = useState<KBSections>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedSection, setExpandedSection] = useState<string>('');
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [showEditor, setShowEditor] = useState(false);
  const [editingItem, setEditingItem] = useState<KBItem | null>(null);
  const [form, setForm] = useState({ section: '', question: '', answer: '', source: '' });
  const [error, setError] = useState('');

  const headers = { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' };

  const load = async () => {
    try {
      const r = await fetch('/api/knowledge-base', { headers });
      if (r.ok) setSections(await r.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleItem = (id: number) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const saveItem = async () => {
    if (!form.section || !form.question || !form.answer) { setError('Section, Question and Answer required'); return; }
    setError('');
    const url = editingItem ? '/api/knowledge-base/' + editingItem.id : '/api/knowledge-base';
    const method = editingItem ? 'PUT' : 'POST';
    try {
      const r = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (r.ok) { setShowEditor(false); setEditingItem(null); setForm({ section: '', question: '', answer: '', source: '' }); load(); }
      else { const d = await r.json(); setError(d.error || 'Save failed'); }
    } catch (e: any) { setError(e.message); }
  };

  const deleteItem = async (id: number) => {
    if (!window.confirm('Delete this KB item?')) return;
    try { await fetch('/api/knowledge-base/' + id, { method: 'DELETE', headers }); load(); }
    catch (e: any) { alert('Delete failed: ' + e.message); }
  };

  const editItem = (item: KBItem, section: string) => {
    setEditingItem(item);
    setForm({ section, question: item.question, answer: item.answer, source: item.source || '' });
    setShowEditor(true);
  };

  const addNew = () => {
    setEditingItem(null);
    setForm({ section: expandedSection || '', question: '', answer: '', source: '' });
    setShowEditor(true);
  };

  // Flatten and filter
  const flatItems: { section: string; item: KBItem }[] = [];
  for (const [sec, items] of Object.entries(sections)) {
    for (const item of items) {
      const matches = !search || item.question.toLowerCase().includes(search.toLowerCase()) || item.answer.toLowerCase().includes(search.toLowerCase());
      if (matches) flatItems.push({ section: sec, item });
    }
  }

  if (loading) return <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Loading knowledge base...</div>;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <h1 style={{ fontSize: '22px', margin: 0 }}>{'\uD83D\uDCDA'} Knowledge Base</h1>
        <button onClick={addNew} style={{ padding: '8px 16px', background: '#2a5298', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>+ Add Article</button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '20px' }}>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search knowledge base..." style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
      </div>

      {/* Error */}
      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}

      {/* Editor Modal */}
      {showEditor && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '600px', padding: '24px', maxHeight: '80vh', overflow: 'auto' }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>{editingItem ? 'Edit Article' : 'Add Article'}</h2>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '4px' }}>Section</label>
              <input type="text" value={form.section} onChange={e => setForm({...form, section: e.target.value})} placeholder="e.g. Getting Started, SERMI Compliance" style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '4px' }}>Question</label>
              <input type="text" value={form.question} onChange={e => setForm({...form, question: e.target.value})} placeholder="The question or topic" style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '4px' }}>Answer</label>
              <textarea value={form.answer} onChange={e => setForm({...form, answer: e.target.value})} rows={5} placeholder="The answer or guide content" style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '4px' }}>Source URL (optional)</label>
              <input type="text" value={form.source} onChange={e => setForm({...form, source: e.target.value})} placeholder="https://..." style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={saveItem} style={{ flex: 1, padding: '12px', background: '#2a5298', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}>Save</button>
              <button onClick={() => { setShowEditor(false); setEditingItem(null); setError(''); }} style={{ padding: '12px 20px', background: 'none', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* No results */}
      {flatItems.length === 0 && <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>{search ? 'No results for "' + search + '"' : 'Knowledge base is empty. Add your first article.'}</div>}

      {/* Sections accordion */}
      {Object.entries(sections).map(([section, items]) => {
        const visibleItems = items.filter(i => !search || i.question.toLowerCase().includes(search.toLowerCase()) || i.answer.toLowerCase().includes(search.toLowerCase()));
        if (visibleItems.length === 0 && search) return null;
        const isExpanded = expandedSection === section;
        return (
          <div key={section} style={{ marginBottom: '12px', background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div onClick={() => setExpandedSection(isExpanded ? '' : section)} style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}>
              <h2 style={{ margin: 0, fontSize: '16px', color: '#1e3c72' }}>{section} <span style={{ fontWeight: 'normal', fontSize: '12px', color: '#94a3b8' }}>({visibleItems.length})</span></h2>
              <span style={{ fontSize: '14px', color: '#94a3b8', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'none' }}>{'\u25BC'}</span>
            </div>
            {isExpanded && (
              <div style={{ borderTop: '1px solid #e2e8f0' }}>
                {visibleItems.map(item => {
                  const isItemExpanded = expandedItems.has(item.id);
                  return (
                    <div key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <div onClick={() => toggleItem(item.id)} style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', color: '#334155', fontWeight: isItemExpanded ? '600' : '400' }}>{item.question}</span>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <button onClick={e => { e.stopPropagation(); editItem(item, section); }} style={{ padding: '4px 8px', background: '#f1f5f9', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', color: '#64748b' }}>{'\u270F\uFE0F'}</button>
                          <button onClick={e => { e.stopPropagation(); deleteItem(item.id); }} style={{ padding: '4px 8px', background: '#fef2f2', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', color: '#dc2626' }}>{'\uD83D\uDDD1\uFE0F'}</button>
                          <span style={{ fontSize: '12px', color: '#94a3b8', transition: 'transform 0.2s', transform: isItemExpanded ? 'rotate(180deg)' : 'none' }}>{'\u25BC'}</span>
                        </div>
                      </div>
                      {isItemExpanded && (
                        <div style={{ padding: '0 20px 16px 20px', fontSize: '14px', color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {item.answer}
                          {item.source && <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '8px', marginBottom: 0 }}>Source: <a href={item.source} target="_blank" style={{ color: '#2563eb' }}>{item.source}</a></p>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default KnowledgeBase;
